<!--
  FRIENDS — who you watch with, what they are watching, and getting into their room.

  ── THE THREE LISTS, IN THE ORDER THEY DESERVE ─────────────────────────────────────

  Somebody waiting for an answer comes first, because leaving a person hanging is the
  only genuinely rude state this screen can be in. Then your friends. Then what you have
  asked for and not got, which is information rather than a task.

  ── FRIENDSHIP IS SYMMETRIC AND NEEDS AN ANSWER ────────────────────────────────────

  Asking creates nothing; accepting creates both edges. The server takes three shortcuts
  a person would expect and this screen has to be ready for all three: asking somebody
  you are already friends with succeeds quietly, asking twice is the same request rather
  than a second one, and asking somebody who has already asked YOU is a yes — which comes
  back as `accepted: true` and means the friends list just grew, not the outbox. That is
  why every action here re-reads the lists instead of patching them locally.

  Private friends hide their presence and invite people explicitly. Public friends
  can still expose a joinable room through presence. Admission is server-enforced.
-->
<script lang="ts">
  import Button from '$lib/ui/Button.svelte';
  import Empty from '$lib/ui/Empty.svelte';
  import Icon from '$lib/ui/Icon.svelte';
  import Spinner from '$lib/ui/Spinner.svelte';
  import { ago } from '$lib/ui/format';
  import { go, setCurrentRoom } from '$lib/ui/nav.svelte';
  import AvatarMark from '$lib/social/AvatarMark.svelte';
  import { personName } from '$lib/social/people';
  import { isWatching, joinableRoom, presenceRank, unsubstantiated } from '$lib/social/presence-line';
  import FriendsPersonCard from '$lib/social/FriendsPersonCard.svelte';
  import { social } from '$lib/social/store';
  import type { PublicProfile } from '$lib/social/protocol';

  const store = social();
  const view = $derived($store);

  $effect(() => {
    store.dismiss();
    void store.load();
  });

  /** Presence, for as long as this screen is looking at it. */
  $effect(() => store.watchPresence());

  /**
   * SAY IT ONCE IF THE SERVER IS STILL OLD.
   *
   * `FriendPresence.watching` is what licenses the words "watching The Matrix", and a
   * service that does not send it makes this screen fall back to the old, wrong
   * meaning (see `presence-line.ts`). That would be invisible — the screen looks
   * exactly the same either way — so it says so in the console rather than being
   * quietly forgotten, in the same shape `live.ts` uses for its own missing hook.
   */
  let warnedAboutWatching = false;
  $effect(() => {
    if (warnedAboutWatching) return;
    if (!Object.values(view.presence).some((p) => unsubstantiated(p))) return;
    warnedAboutWatching = true;
    console.warn(
      '[social] this service does not send FriendPresence.watching, so "watching X" is\n' +
        'still being inferred from the room having a title — which is exactly the defect\n' +
        'the owner reported (a device that left the player kept saying "watching"). One\n' +
        'change, in RoomHub.watchingByUser(): set `watching` from the member\'s own state,\n' +
        "true only for 'buffering' | 'ready' | 'playing' | 'stalled'. The contract and the\n" +
        'measurements behind it are in web/src/lib/social/protocol.ts.'
    );
  });

  /* ── search ──────────────────────────────────────────────────────────── */

  let query = $state('');
  let results = $state<PublicProfile[]>([]);
  let searching = $state(false);
  let searched = $state('');
  let searchInput = $state<HTMLInputElement>();
  let copyState = $state<'idle' | 'copied' | 'failed'>('idle');
  async function copyHandle() {
    if (!view.me) return;
    try {
      await navigator.clipboard.writeText(`@${view.me.handle}`);
      copyState = 'copied';
    } catch { copyState = 'failed'; }
  }

  $effect(() => {
    const q = query.trim().replace(/^@+/, '');
    if (q.length < 2) {
      results = [];
      searched = '';
      searching = false;
      return;
    }
    searching = true;
    results = [];
    const abort = new AbortController();
    const timer = setTimeout(() => {
      void store.search(q, abort.signal).then((found) => {
        if (abort.signal.aborted) return;
        results = found;
        searched = q;
        searching = false;
      });
    }, 300);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  });

  let now = $state(Date.now());
  $effect(() => {
    const timer = setInterval(() => { now = Date.now(); }, 1000);
    return () => clearInterval(timer);
  });
  async function answerInvite(id: string, accept: boolean): Promise<void> {
    const roomId = await store.respondInvite(id, accept);
    if (roomId) {
      setCurrentRoom(roomId);
      go(`/room/${encodeURIComponent(roomId)}`);
    }
  }

  const friendIds = $derived(new Set(view.friends.map((f) => f.userId)));
  const askedIds = $derived(new Set(view.outgoing.map((r) => r.to.userId)));
  const askingMeIds = $derived(new Set(view.incoming.map((r) => r.from.userId)));

  async function add(person: PublicProfile): Promise<void> {
    const ok = await store.addFriend({ userId: person.userId });
    if (ok) {
      query = '';
      results = [];
    }
  }

  /* ── joining ─────────────────────────────────────────────────────────── */

  /**
   * Public activity exposes a joinable room only when privacy allows it.
   * Private rooms are entered through the invitation mailbox instead.
   */
  function joinFriend(friend: PublicProfile): void {
    const roomId = joinableRoom(view.presence[friend.userId]);
    if (roomId === null) return;
    setCurrentRoom(roomId);
    go(`/room/${encodeURIComponent(roomId)}`);
  }

  const presentFriends = $derived(
    [...view.friends].sort(
      (a, b) =>
        presenceRank(view.presence[a.userId]) - presenceRank(view.presence[b.userId]) ||
        personName(a).localeCompare(personName(b))
    )
  );

  /**
   * The heading only claims somebody is watching when somebody IS — the same rule the
   * rows use, read from the same function. It used to be `presence[...].roomId`, which
   * is a membership and not a player, so the screen announced "Watching now" over a
   * list of people who had all gone to bed with the tab open.
   */
  const anyoneWatching = $derived(view.friends.some((f) => isWatching(view.presence[f.userId])));
</script>

<svelte:head><title>Friends · Jovii</title></svelte:head>

<div class="page">
  <header class="page-heading">
    <div><h1>Friends</h1><p class="intro">A good watch is better with company.</p></div>
    {#if view.me}
      <a class="privacy-link" href="/profile"><Icon name="user" size={16} />{view.me.privateMode !== false ? 'Private mode on' : 'Activity shared with friends'}<Icon name="forward" size={13} /></a>
    {/if}
  </header>

  {#if view.phase === 'loading' || view.phase === 'idle'}
    <div class="loading" role="status"><Spinner size={20} /><p>Loading your friends…</p></div>
  {:else if view.phase === 'unavailable' || view.me === null}
    <Empty icon="people" title="Friends couldn’t load" detail={view.error ?? 'Your profile and invitations are unavailable right now. Try again in a moment.'}>
      <Button icon="refresh" loading={view.busy.includes('load')} onclick={() => store.load(true)}>Try again</Button>
    </Empty>
  {:else}
    <div class="connect">
      <section class="find-section" aria-labelledby="find-heading">
        <h2 id="find-heading">Add a friend</h2>
        <div class="find">
          <Icon name="search" size={20} />
          <input bind:this={searchInput} type="search" name="friend-search" bind:value={query} placeholder="Search by handle or name…" autocomplete="off" autocapitalize="none" spellcheck="false" aria-label="Search for a friend by handle or name" aria-describedby="search-help" />
          {#if searching}<Spinner size={18} />{/if}
        </div>
        <p class="fine" id="search-help">Type at least 2 characters to find someone on Jovii.</p>
      </section>
      <section class="share-handle" aria-label="Your handle">
        <AvatarMark avatar={view.me.avatar} color={view.me.color} size={48} square />
        <div class="handle-copy"><span class="fine">Share your handle</span><strong>@{view.me.handle}</strong></div>
        <Button icon={copyState === 'copied' ? 'check' : 'link'} onclick={copyHandle}>{copyState === 'copied' ? 'Copied' : 'Copy'}</Button>
        {#if copyState !== 'idle'}<p class="copy-note fine" role="status">{copyState === 'copied' ? 'Handle copied. Send it to a friend.' : 'Couldn’t copy. Select your handle above to copy it manually.'}</p>{/if}
      </section>
    </div>

    {#if searching}<p class="fine search-status" role="status">Searching…</p>{/if}
    {#if results.length > 0}
      <section class="results" aria-label="Search results">
        <p class="fine" role="status">{results.length} {results.length === 1 ? 'person found' : 'people found'}</p>
        <ul class="people">
          {#each results as person (person.userId)}
            <li>
              <AvatarMark avatar={person.avatar} color={person.color} size={44} square />
              <span class="who"><b>{personName(person)}</b><span>@{person.handle}</span></span>
              {#if friendIds.has(person.userId)}
                <span class="already"><Icon name="check" size={15} />Friends</span>
              {:else if askedIds.has(person.userId)}
                <span class="already">Request sent</span>
              {:else}
                <Button variant="primary" icon={askingMeIds.has(person.userId) ? 'check' : 'plus'} loading={view.busy.includes(`friend:${person.userId}`)} onclick={() => add(person)}>{askingMeIds.has(person.userId) ? 'Accept' : 'Add friend'}</Button>
              {/if}
            </li>
          {/each}
        </ul>
      </section>
    {:else if searched !== '' && !searching && !view.error}
      <p class="search-status fine" role="status">No matches for “{searched}”. Check the spelling or ask your friend for their handle.</p>
    {/if}

    {#if view.error !== null}
      <div class="feedback bad" role="alert"><Icon name="alert" size={18} /><p>{view.error}</p><Button loading={view.busy.includes('load')} onclick={() => store.load(true)}>Retry</Button></div>
    {:else if view.notice !== null}
      <p class="feedback" role="status"><Icon name="check" size={18} />{view.notice}</p>
    {/if}

    <section class="inbox" aria-labelledby="inbox-heading">
      <div class="section-heading"><h2 id="inbox-heading">Invitations</h2><span class="count">{view.roomInvites.length + view.incoming.length}</span></div>
      {#if view.inviteError}
        <div class="feedback bad" role="alert"><p>{view.inviteError}</p><Button onclick={() => store.refreshInvites()}>Refresh invitations</Button></div>
      {/if}
      {#if view.roomInvites.length > 0}
        <h3 class="list-label">Watch together</h3>
        <ul class="people invitations">
          {#each view.roomInvites as invite (invite.id)}
            {@const expired = invite.expiresAt <= now + view.skewMs}
            <li class:expired>
              <AvatarMark avatar={invite.from.avatar} color={invite.from.color} size={48} square />
              <span class="who"><b>{personName(invite.from)}</b><span>{expired ? 'Invitation expired' : 'Invited you to watch together'}</span></span>
              <span class="pair">
                <Button disabled={view.busy.includes(`invite:${invite.id}`)} onclick={() => answerInvite(invite.id, false)}>{expired ? 'Dismiss' : 'Decline'}</Button>
                <Button variant="primary" icon="play" loading={view.busy.includes(`invite:${invite.id}`)} disabled={expired} onclick={() => answerInvite(invite.id, true)}>Accept & join</Button>
              </span>
            </li>
          {/each}
        </ul>
      {/if}
      {#if view.incoming.length > 0}
        <h3 class="list-label">Friend requests</h3>
        <ul class="people">
          {#each view.incoming as req (req.id)}
            <li>
              <AvatarMark avatar={req.from.avatar} color={req.from.color} size={44} square />
              <span class="who"><b>{personName(req.from)}</b><span>@{req.from.handle} · {ago(req.createdAt)}</span></span>
              <span class="pair">
                <Button disabled={view.busy.includes(`request:${req.id}`)} onclick={() => store.respond(req.id, false)}>Decline</Button>
                <Button variant="primary" loading={view.busy.includes(`request:${req.id}`)} onclick={() => store.respond(req.id, true)}>Accept</Button>
              </span>
            </li>
          {/each}
        </ul>
        <p class="fine">Declining a friend request is silent. They can ask again.</p>
      {/if}
      {#if view.roomInvites.length === 0 && view.incoming.length === 0 && !view.inviteError}
        <p class="inbox-empty"><Icon name="check" size={18} /><span>No invitations waiting. When a friend invites you, you can join them here.</span></p>
      {/if}
    </section>

    <section class="friends-section" aria-labelledby="friends-heading">
      <div class="section-heading"><h2 id="friends-heading">Your friends</h2><span class="count">{view.friends.length}</span></div>
      {#if view.friends.length > 0}
        <p class="section-note">{anyoneWatching ? 'See what’s on and join a friend.' : 'Private friends can invite you without sharing their activity.'}</p>
        <ul class="friend-grid">
          {#each presentFriends as friend (friend.userId)}
            <FriendsPersonCard {friend} presence={view.presence[friend.userId]} busy={view.busy.includes(`friend:${friend.userId}`)} onjoin={joinFriend} onremove={(f) => store.unfriend(f.userId)} />
          {/each}
        </ul>
      {:else}
        <div class="first-friend">
          <Icon name="people" size={32} />
          <h3>Who’s watching with you?</h3>
          <p>Add someone you know or send them your handle. Once you’re friends, you can invite each other from the player.</p>
          <Button variant="primary" icon="plus" onclick={() => searchInput?.focus()}>Find your first friend</Button>
        </div>
      {/if}
    </section>

    {#if view.outgoing.length > 0}
      <details class="sent">
        <summary><span>Sent requests <span class="count">{view.outgoing.length}</span></span><Icon name="down" size={18} /></summary>
        <ul class="people">
          {#each view.outgoing as req (req.id)}
            <li>
              <AvatarMark avatar={req.to.avatar} color={req.to.color} size={40} square />
              <span class="who"><b>{personName(req.to)}</b><span>@{req.to.handle} · sent {ago(req.createdAt)}</span></span>
              <Button loading={view.busy.includes(`request:${req.id}`)} onclick={() => store.cancel(req.id)}>Withdraw</Button>
            </li>
          {/each}
        </ul>
        <p class="fine">Waiting for a reply. Friend requests don’t expire.</p>
      </details>
    {/if}
    <footer class="privacy"><Icon name="info" size={17} /><p>Private mode hides your online status and what you watch. Friends need your invitation to join. <a href="/profile">Manage privacy</a></p></footer>
  {/if}
</div>

<style>
  .page { max-width: 1100px; margin-inline: auto; padding: var(--space-7) max(var(--page-pad), var(--safe-r)) var(--space-7) max(var(--page-pad), var(--safe-l)); }
  .page-heading { display: flex; justify-content: space-between; align-items: center; gap: var(--space-5); margin-bottom: var(--space-7); }
  h1 { font-size: var(--text-3xl); font-weight: var(--weight-bold); letter-spacing: var(--tracking-tight); line-height: var(--leading-tight); }
  .intro { margin-top: var(--space-2); color: var(--text-dim); }
  .privacy-link { display: inline-flex; align-items: center; gap: var(--space-2); min-height: 44px; color: var(--text-dim); font-size: var(--text-sm); }
  .privacy-link:hover, .privacy a:hover { color: var(--text); }
  .connect { display: grid; grid-template-columns: 1.15fr 1fr; align-items: center; gap: var(--space-7); padding-bottom: var(--space-6); border-bottom: 1px solid var(--line); }
  h2 { font-size: var(--text-lg); font-weight: var(--weight-semi); letter-spacing: var(--tracking-snug); }
  .find { display: flex; align-items: center; gap: var(--space-3); margin-top: var(--space-3); padding: 0 var(--space-4); border: 1px solid var(--line); background: var(--surface-1); border-radius: var(--radius-pill); color: var(--text-dim); }
  .find:focus-within { outline: 2px solid var(--focus-ring); outline-offset: 3px; }
  .find input { min-width: 0; width: 100%; height: 50px; border: 0; background: transparent; padding: 0; color: var(--text); font-size: var(--text-md); outline: none; }
  .find input::placeholder { color: var(--text-faint); }
  .fine { color: var(--text-dim); font-size: var(--text-sm); line-height: var(--leading); }
  .find-section > .fine { margin-top: var(--space-2); }
  .share-handle { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-3); min-width: 0; }
  .handle-copy { flex: 1; min-width: 90px; }
  .handle-copy strong { display: block; font-size: var(--text-base); overflow-wrap: anywhere; user-select: all; }
  .copy-note { flex-basis: 100%; }
  .results { padding: var(--space-5); margin-top: var(--space-4); background: var(--surface-1); border-radius: var(--radius-lg); }
  .search-status { margin-top: var(--space-4); overflow-wrap: anywhere; }
  .people, .friend-grid { list-style: none; padding: 0; margin: 0; }
  .people > li { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-4) 0; min-width: 0; }
  .people > li + li { border-top: 1px solid var(--line); }
  .who { display: block; flex: 1; min-width: 0; }
  .who b { display: block; font-size: var(--text-base); font-weight: var(--weight-semi); overflow-wrap: anywhere; }
  .who > span { display: block; color: var(--text-dim); font-size: var(--text-sm); margin-top: var(--space-1); overflow-wrap: anywhere; }
  .pair, .already { display: flex; align-items: center; gap: var(--space-2); flex: none; }
  .already { color: var(--text-dim); font-size: var(--text-sm); }
  .feedback { display: flex; flex-wrap: wrap; gap: var(--space-3); align-items: center; margin-block: var(--space-4); padding: var(--space-4); background: var(--surface-1); border-radius: var(--radius); color: var(--text); font-size: var(--text-sm); }
  .feedback p { flex: 1; overflow-wrap: anywhere; }
  .bad { border: 1px solid var(--bad); }
  .section-heading { display: flex; align-items: center; gap: var(--space-3); }
  .count { color: var(--text-dim); font-size: var(--text-sm); font-weight: var(--weight-normal); font-variant-numeric: tabular-nums; }
  .inbox { padding-block: var(--space-6); border-bottom: 1px solid var(--line); }
  .list-label { font-size: var(--text-sm); color: var(--text-dim); font-weight: var(--weight-medium); margin-top: var(--space-5); }
  .invitations > li { padding: var(--space-4); margin-top: var(--space-3); background: var(--surface-1); border: 1px solid color-mix(in oklab, var(--accent) 35%, var(--line)); border-radius: var(--radius-lg); }
  .invitations > li.expired { border-color: var(--line); }
  .inbox-empty { display: flex; align-items: center; gap: var(--space-3); margin-top: var(--space-3); color: var(--text-dim); font-size: var(--text-sm); }
  .friends-section { margin-top: var(--space-6); }
  .section-note { margin-top: var(--space-2); color: var(--text-dim); font-size: var(--text-sm); }
  .friend-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: start; gap: var(--space-4); margin-top: var(--space-5); }
  .first-friend { padding: var(--space-7) 0; max-width: 48ch; }
  .first-friend > :global(svg) { color: var(--text-dim); }
  .first-friend h3 { font-size: var(--text-2xl); line-height: var(--leading-snug); letter-spacing: var(--tracking-tight); margin-top: var(--space-4); }
  .first-friend p { color: var(--text-dim); margin: var(--space-3) 0 var(--space-5); }
  .sent { border-top: 1px solid var(--line); margin-top: var(--space-6); padding-top: var(--space-2); }
  summary { display: flex; justify-content: space-between; align-items: center; min-height: 52px; gap: var(--space-3); cursor: pointer; font-size: var(--text-base); font-weight: var(--weight-medium); list-style: none; }
  summary::-webkit-details-marker { display: none; }
  summary .count { margin-left: var(--space-2); }
  summary:hover { color: var(--text-dim); }
  details[open] summary :global(svg) { transform: rotate(180deg); }
  .privacy { display: flex; gap: var(--space-3); align-items: flex-start; margin-top: var(--space-7); padding-top: var(--space-5); border-top: 1px solid var(--line); color: var(--text-dim); font-size: var(--text-sm); max-width: 75ch; }
  .privacy > :global(svg) { flex: none; margin-top: 2px; }
  .privacy a { color: var(--text); text-decoration: underline; text-underline-offset: 3px; }
  .loading { display: flex; align-items: center; gap: var(--space-3); min-height: 200px; color: var(--text-dim); }
  a:focus-visible, summary:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 4px; }
  @media (max-width: 767px) {
    .page { padding-top: var(--space-5); }
    .page-heading { display: block; margin-bottom: var(--space-5); }
    h1 { font-size: var(--text-2xl); }
    .privacy-link { margin-top: var(--space-2); }
    .connect { grid-template-columns: 1fr; gap: var(--space-5); }
    .share-handle { padding-top: var(--space-4); border-top: 1px solid var(--line); }
    .friend-grid { grid-template-columns: 1fr; }
    .people > li { flex-wrap: wrap; }
    .pair { flex-basis: 100%; justify-content: flex-end; }
    .pair :global(.btn) { flex: 1; min-height: 44px; }
    .invitations > li { padding: var(--space-4); }
    .results { padding: var(--space-4); }
    .results .people > li > :global(.btn) { max-width: 100%; }
    .first-friend { padding-block: var(--space-6); }
  }
  @media (prefers-reduced-motion: reduce) { .page :global(*) { transition-duration: 0s !important; animation-duration: 0s !important; } }
</style>
